import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import {
  Capsule,
  CommentAuthor,
  Comment,
  capsulesApi,
} from '@/services/capsulesApi';
import M_SelectPicker from '../M_Molecules/M_SelectPicker/M_SelectPicker';
import Q_BlurEffect from '../Q_Quarks/Q_BlurEffect/Q_BlurEffect';
import MockImage from '@/mocks/assets/tree/A_ImagePark1.jpg';
import M_AddReaction from '@/components/M_Molecules/M_AddReaction/M_AddReaction';
import M_CapsuleCommentMobilePreview from '@/components/M_Molecules/M_CapsuleCommentMobilePreview/M_CapsuleCommentMobilePreview';
import { useTranslation } from '@/hooks/useTranslation';
import styles from './CapsuleReactionsView.module.css';
import TunnelBackground from './TunnelBackground';
import LiveTicker from './LiveTicker';
import { useAuth } from '@/contexts/AuthContext';
import ReactionFallback from '@/assets/images/A_UserAvatar.png';
import { getMediaUrl } from '@/utils/mediaUrl';
import O_CapsuleReactionsHeaderResponsive from '../O_Organisms/O_CapsuleReactionsHeader/O_CapsuleReactionsHeaderResponsive';
import O_CapsuleCreateHeaderResponsive from '../O_Organisms/O_CapsuleCreateHeader/O_CapsuleCreateHeaderResponsive';
import { MockPost } from '@/mocks/contentMocks';
import O_CapsuleCommentMobilePost from '../O_Organisms/O_CapsuleCommentMobilePost/O_CapsuleCommentMobilePost';
import { CapsuleViewMode, getCapsuleViewOptions } from './constants';
import { useIsMobile } from '@/hooks/useIsMobile';
import O_CapsulePostMenu from '../O_Organisms/O_CapsulePostMobile/O_CapsulePostMobile';
import {
  getScenarioById,
  CAPSULE_SCENARIOS,
} from '@/constants/capsuleScenarios';
import { useDeleteCapsule } from '@/hooks/useCapsules';
import { PATHS } from '@/constants/paths';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import { commentStonesApi } from '@/services/commentStonesApi';
import { resolveCommentStoneImage } from '@/utils/commentStones';
import ConfirmStonePurchaseModal from './ConfirmStonePurchaseModal';
import { CapsuleReaction, ReactionReply } from './types';
import { useTunnelStones } from './useTunnelStones';
import TunnelCenterPanel from './TunnelCenterPanel';
import CapsuleCommentSidebar from './CapsuleCommentSidebar';

interface CapsuleReactionsViewProps {
  capsule: Capsule;
  isFullscreen: boolean;
  selectedView: CapsuleViewMode;
  onViewSelect: (option: CapsuleViewMode) => void;
  mode?: 'default' | 'preview';
  isScenarioPreview?: boolean;
  onPreviewClick?: () => void;
  onPublish?: () => void;
  headerSlot?: ReactNode;
  backgroundMode?: 'image' | 'tunnel';
}

export const CapsuleReactionsView: React.FC<CapsuleReactionsViewProps> = ({
  capsule,
  isFullscreen,
  selectedView,
  onViewSelect,
  mode = 'default',
  isScenarioPreview = false,
  onPreviewClick,
  onPublish,
  headerSlot,
  backgroundMode = 'image',
}) => {
  const [localReactions, setLocalReactions] = useState<CapsuleReaction[]>([]);
  const [activeReactionId, setActiveReactionId] = useState<string | null>(null);
  const [isExpandedView, setIsExpandedView] = useState(false);
  const [isDraggingNew, setIsDraggingNew] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [rightPanelMaxHeight, setRightPanelMaxHeight] = useState<number | null>(
    null
  );
  const [isMobileCommentPreviewVisible, setIsMobileCommentPreviewVisible] =
    useState(false);
  const [isMobileCommentPostOpen, setIsMobileCommentPostOpen] = useState(false);
  const [tunnelCenterView, setTunnelCenterView] = useState<
    'stones' | 'comment'
  >('stones');
  const [isPurchasingStone, setIsPurchasingStone] = useState(false);
  const [purchasedStoneIds, setPurchasedStoneIds] = useState<string[]>([]);
  const [isPurchaseConfirmOpen, setIsPurchaseConfirmOpen] = useState(false);
  const [stoneNotification, setStoneNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const pendingCommentSubmitRef = useRef(false);
  const pendingStoneIdRef = useRef<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const bottomControlsRef = useRef<HTMLDivElement>(null);
  const tunnelCenterScreenRef = useRef<HTMLDivElement>(null);
  const t = useTranslation();
  const viewOptions = useMemo(() => getCapsuleViewOptions(t), [t]);
  const viewOptionLabels = useMemo(
    () => viewOptions.map(({ label }) => label),
    [viewOptions]
  );
  const selectedViewLabel = useMemo(
    () =>
      viewOptions.find(({ key }) => key === selectedView)?.label ||
      viewOptions[0]?.label ||
      '',
    [selectedView, viewOptions]
  );
  const [isPostMenuOpen, setIsPostMenuOpen] = useState(false);
  const isMobile = useIsMobile(1080);
  const navigate = useNavigate();
  const deleteCapsuleMutation = useDeleteCapsule();
  const { user, getCurrentUser } = useAuth();
  const {
    tunnelStoneOptions,
    selectedStoneId,
    selectedTunnelStone,
    tunnelStoneTextureUrls,
    isLoadingTunnelStones,
    handleTunnelStoneSelect,
  } = useTunnelStones({ backgroundMode });

  const tunnelReactions = useMemo(() => {
    const out = localReactions
      .filter(reaction => Boolean(reaction.text && reaction.author?.name))
      .map(reaction => {
        const imageUrl = resolveCommentStoneImage(
          reaction.commentObject as string,
          tunnelStoneOptions
        );
        return {
          id: reaction.id,
          user: reaction.author.name,
          text: reaction.text,
          imageUrl,
          avatarUrl: reaction.author.avatar
            ? getMediaUrl(reaction.author.avatar)
            : undefined,
        };
      });
    return out;
  }, [localReactions, tunnelStoneOptions]);

  const isAuthor = Boolean(
    user?.id &&
      capsule.author?.id &&
      String(capsule.author.id) === String(user.id)
  );

  const handleDeleteCapsule = useCallback(async () => {
    try {
      await deleteCapsuleMutation.mutate(String(capsule.id));
      navigate(PATHS.CAPSULES, { state: { notification: 'deleteSuccess' } });
    } catch {
      // ошибка удаления
    }
  }, [capsule.id, deleteCapsuleMutation, navigate]);

  const isPreview = mode === 'preview';
  const isAuthenticated = isPreview ? false : Boolean(user);

  const buildAuthor = useCallback((): CommentAuthor => {
    const fallbackName = t('capsuleReactions.anonymous');
    if (!user) {
      return {
        id: 'temp-user',
        name: fallbackName,
      };
    }

    const firstName = user.personal.firstName?.trim() || '';
    const lastName = user.personal.lastName?.trim() || '';
    const nameFromPersonal = [firstName, lastName].filter(Boolean).join(' ');
    return {
      id: user.id,
      name:
        nameFromPersonal || user.handle || user.contact.email || fallbackName,
      avatar: user.avatar || ReactionFallback,
    };
  }, [user, t]);

  const scenario = capsule.scenario;

  const enhancedScenario = useMemo(() => {
    if (!scenario?.id) {
      return scenario;
    }

    const scenarioOption = getScenarioById(scenario.id);

    if (!scenarioOption) {
      const scenariosWithStatus = CAPSULE_SCENARIOS;
      const defaultScenario = scenariosWithStatus.find(s => s.unlocked);
      if (!defaultScenario) {
        return scenario;
      }

      return {
        ...scenario,
        backgroundUrl:
          scenario.backgroundUrl && scenario.backgroundUrl !== null
            ? scenario.backgroundUrl
            : defaultScenario.background,
        commentObject:
          scenario.commentObject &&
          scenario.commentObject !== null &&
          scenario.commentObject !== 'capsule' &&
          (scenario.commentObject.startsWith('/') ||
            scenario.commentObject.startsWith('http'))
            ? scenario.commentObject
            : defaultScenario.commentObject,
      };
    }

    const backgroundUrl =
      scenario.backgroundUrl && scenario.backgroundUrl !== null
        ? scenario.backgroundUrl
        : scenarioOption.background;

    const commentObject =
      scenario.commentObject &&
      scenario.commentObject !== null &&
      scenario.commentObject !== 'capsule' &&
      (scenario.commentObject.startsWith('/') ||
        scenario.commentObject.startsWith('http'))
        ? scenario.commentObject
        : scenarioOption.commentObject;

    return {
      ...scenario,
      backgroundUrl,
      commentObject,
    };
  }, [scenario]);

  const scenarioCommentObject = useMemo(
    () => enhancedScenario?.commentObject ?? ReactionFallback,
    [enhancedScenario]
  );
  const backgroundSrc = enhancedScenario?.backgroundUrl ?? MockImage;

  const convertCommentToReaction = useCallback(
    (
      comment: Comment & {
        position?: { top: number; left: number };
        comment_object?: string | number;
        commentObject?: string | number;
      }
    ): CapsuleReaction => {
      const replies: ReactionReply[] =
        comment.replies?.map(reply => ({
          id: reply.id,
          text: reply.text,
          createdAt: reply.createdAt,
          author: reply.author,
        })) || [];
      const stoneUrl =
        (comment as CapsuleReaction).commentObject ??
        comment.comment_object ??
        comment.commentObject ??
        scenarioCommentObject;

      return {
        id: comment.id,
        text: comment.text,
        createdAt: comment.createdAt,
        author: comment.author,
        position: comment.position,
        commentObject: stoneUrl != null ? String(stoneUrl) : undefined,
        replies,
        isGuest: false,
      };
    },
    [scenarioCommentObject]
  );

  const submitCommentCore = useCallback(async () => {
    let currentReactionId = activeReactionId;
    let selectedReaction = localReactions.find(r => r.id === currentReactionId);

    const shouldCreateTunnelReaction =
      backgroundMode === 'tunnel' &&
      isAuthenticated &&
      selectedTunnelStone?.id &&
      (!selectedReaction || Boolean(selectedReaction.text));

    if (shouldCreateTunnelReaction) {
      const newReaction: CapsuleReaction = {
        id: `temp-${Date.now()}`,
        text: '',
        createdAt: new Date().toISOString(),
        author: buildAuthor(),
        position: { top: 50, left: 50 },
        commentObject: selectedTunnelStone.id,
        replies: [],
        isGuest: false,
      };
      setLocalReactions(prev => [...prev, newReaction]);
      setActiveReactionId(newReaction.id);
      currentReactionId = newReaction.id;
      selectedReaction = newReaction;
    }

    if (!currentReactionId || !selectedReaction) return;

    const nextText = commentDraft.trim();

    if (!currentReactionId.startsWith('temp-') && selectedReaction.text) {
      return;
    }

    if (currentReactionId.startsWith('temp-')) {
      try {
        const result = await capsulesApi.addComment(capsule.id, {
          text: nextText,
          position: selectedReaction.position,
          ...(selectedReaction.commentObject && {
            comment_object: selectedReaction.commentObject,
          }),
        });

        if (result.success && result.data) {
          const newReaction = convertCommentToReaction(result.data);
          if (selectedReaction.position && !newReaction.position) {
            newReaction.position = selectedReaction.position;
          }
          const savedCommentObject =
            selectedReaction.commentObject ??
            (newReaction as CapsuleReaction).commentObject;
          (newReaction as CapsuleReaction).commentObject = savedCommentObject;
          setLocalReactions(prev =>
            prev.map(reaction =>
              reaction.id === currentReactionId ? newReaction : reaction
            )
          );
          if (newReaction.id !== currentReactionId) {
            setActiveReactionId(newReaction.id);
          }
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.detail ??
          error?.data?.detail ??
          error?.message ??
          t('capsuleReactions.purchaseStoneError') ??
          'Не удалось отправить комментарий';

        setStoneNotification({
          type: 'error',
          message,
        });
      }
    } else {
      if (selectedReaction.text && replyDraft.trim()) {
        return;
      }

      try {
        const result = await capsulesApi.updateComment(
          capsule.id,
          currentReactionId,
          { text: nextText }
        );

        if (result.success && result.data) {
          const updatedReaction = convertCommentToReaction(result.data);
          (updatedReaction as CapsuleReaction).commentObject =
            selectedReaction.commentObject ??
            (updatedReaction as CapsuleReaction).commentObject;
          setLocalReactions(prev =>
            prev.map(reaction =>
              reaction.id === currentReactionId ? updatedReaction : reaction
            )
          );
        }
      } catch {
        // ошибка обновления комментария
      }
    }

    setCommentDraft('');
  }, [
    activeReactionId,
    backgroundMode,
    buildAuthor,
    capsule.id,
    commentDraft,
    convertCommentToReaction,
    isAuthenticated,
    localReactions,
    replyDraft,
    selectedTunnelStone,
    t,
  ]);

  const handlePurchaseConfirm = useCallback(async () => {
    const pendingStoneId = pendingStoneIdRef.current ?? selectedTunnelStone?.id;
    if (!pendingStoneId) return;

    const isAlreadyPurchased = purchasedStoneIds.includes(pendingStoneId);
    setIsPurchaseConfirmOpen(false);

    if (isAlreadyPurchased) {
      if (pendingCommentSubmitRef.current) {
        pendingCommentSubmitRef.current = false;
        await submitCommentCore();
      }
      return;
    }

    setIsPurchasingStone(true);
    try {
      await commentStonesApi.purchaseCommentStone(pendingStoneId);
      setPurchasedStoneIds(prev => {
        return prev.includes(pendingStoneId) ? prev : [...prev, pendingStoneId];
      });
      setStoneNotification({
        type: 'success',
        message: t('capsuleReactions.purchaseStoneSuccess') || 'Камень куплен',
      });
      await getCurrentUser();
      if (pendingCommentSubmitRef.current) {
        pendingCommentSubmitRef.current = false;
        await submitCommentCore();
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.detail ??
        error?.data?.detail ??
        error?.message ??
        t('capsuleReactions.purchaseStoneError') ??
        'Не удалось купить камень';
      setStoneNotification({
        type: 'error',
        message,
      });
    } finally {
      setIsPurchasingStone(false);
    }
  }, [
    getCurrentUser,
    purchasedStoneIds,
    selectedTunnelStone,
    submitCommentCore,
    t,
  ]);

  const selectedComment = useMemo(
    () =>
      activeReactionId
        ? localReactions.find(reaction => reaction.id === activeReactionId) ||
          null
        : null,
    [activeReactionId, localReactions]
  );

  const capsuleMock = useMemo<MockPost>(() => {
    const baseAuthorAvatar = capsule.author.avatar ?? ReactionFallback;
    const coauthorAvatars = capsule.privacy.coauthors?.map(
      coauthor => coauthor.avatar || baseAuthorAvatar
    ) || [baseAuthorAvatar];

    return {
      id: capsule.id,
      title: capsule.title || '',
      content: capsule.description || '',
      author: capsule.author.name,
      authorAvatar: baseAuthorAvatar,
      timestamp: capsule.meta.createdAt || '',
      likes: capsule.engagement.comments,
      comments: localReactions.length,
      imageUrl: scenarioCommentObject,
      avatars: coauthorAvatars,
      coauthors: capsule.privacy.coauthors?.length || 0,
      isMyCapsule: false,
      ownerName: capsule.author.name,
      ownerId: capsule.author.id,
      createdAt: capsule.meta.createdAt || '',
      firstOpenedAt: capsule.meta.firstOpenedAt || '',
      treeId: capsule.tree?.id,
      capsulePostAuthorDate: {
        avatars: coauthorAvatars,
        coauthors: capsule.privacy.coauthors?.length || 0,
        date: capsule.meta.createdAt || '',
        isMyCapsule: false,
        ownerName: capsule.author.name,
        ownerId: capsule.author.id,
      },
      capsulePostContent: {
        title: capsule.title || '',
        text: capsule.description || '',
        commentCount: localReactions.length,
        plantCount: 0,
        indicator: 'comment',
      },
      capsulePostProgress: undefined,
      scenario: {
        id: enhancedScenario?.id || capsule.id,
        backgroundUrl: backgroundSrc,
        commentObject: scenarioCommentObject,
        comments: localReactions,
      },
    };
  }, [
    capsule,
    localReactions,
    enhancedScenario,
    scenarioCommentObject,
    backgroundSrc,
  ]);

  const lastCapsuleIdRef = useRef<string | null>(null);
  useEffect(() => {
    const capsuleId = String(capsule.id);
    if (lastCapsuleIdRef.current === capsuleId) {
      return;
    }
    lastCapsuleIdRef.current = capsuleId;

    const scenarioComments = enhancedScenario?.comments ?? [];

    if (scenarioComments.length > 0) {
      const mapped = scenarioComments.map<CapsuleReaction>((comment: any) => {
        const stoneUrl =
          comment.commentObject ??
          comment.comment_object ??
          scenarioCommentObject;
        return {
          ...comment,
          commentObject: stoneUrl != null ? String(stoneUrl) : undefined,
          replies: (comment as CapsuleReaction).replies ?? [],
          isGuest: Boolean((comment as CapsuleReaction).isGuest),
        };
      });
      setLocalReactions(mapped);
      setActiveReactionId(prevId => {
        if (prevId && mapped.some(c => c.id === prevId)) {
          return prevId;
        }
        return mapped[0]?.id ?? null;
      });
      return;
    }

    capsulesApi
      .getComments(capsuleId, { limit: 200 })
      .then(res => {
        if (lastCapsuleIdRef.current !== capsuleId) return;
        if (!res.success || !res.data?.comments?.length) {
          setLocalReactions([]);
          setActiveReactionId(null);
          return;
        }
        const mapped = res.data.comments.map(
          (
            c: Comment & {
              position?: { top: number; left: number };
              comment_object?: string | number;
              commentObject?: string | number;
            }
          ) => convertCommentToReaction(c)
        );
        setLocalReactions(mapped);
        setActiveReactionId(mapped[0]?.id ?? null);
      })
      .catch(() => {
        if (lastCapsuleIdRef.current !== capsuleId) return;
        setLocalReactions([]);
        setActiveReactionId(null);
      });
  }, [
    capsule.id,
    enhancedScenario,
    scenarioCommentObject,
    convertCommentToReaction,
  ]);

  useEffect(() => {
    if (!localReactions.length) {
      setActiveReactionId(null);
      return;
    }

    if (
      !activeReactionId ||
      !localReactions.some(reaction => reaction.id === activeReactionId)
    ) {
      const nextReaction = localReactions[0];
      setActiveReactionId(nextReaction.id);
      setIsDraggingNew(false);
    }
  }, [localReactions, activeReactionId]);

  useEffect(() => {
    if (!selectedComment) {
      setCommentDraft('');
      setReplyDraft('');
      return;
    }

    if (!isAuthenticated) {
      setCommentDraft('');
      setReplyDraft('');
      return;
    }

    if (selectedComment.text) {
      setCommentDraft('');
    }

    setReplyDraft('');
  }, [selectedComment, isAuthenticated]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const controlsHeight = bottomControlsRef.current?.offsetHeight ?? 0;
      const nextHeight = window.innerHeight - controlsHeight - 32;
      setRightPanelMaxHeight(nextHeight > 0 ? nextHeight : null);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handlePan = (_e: React.MouseEvent) => {};

  const handleReactionSelect = useCallback(
    (reactionId: string) => {
      setActiveReactionId(reactionId);
      setIsExpandedView(false);
      setIsDraggingNew(false);
      const selected = localReactions.find(item => item.id === reactionId);
      if (selected?.text) {
        setTunnelCenterView('comment');
      }
      if (
        selected &&
        isAuthenticated &&
        selected.author.id === user?.id &&
        !selected.text
      ) {
        setCommentDraft('');
      } else {
        setCommentDraft('');
      }
      setReplyDraft('');
      if (isMobile) {
        setIsMobileCommentPreviewVisible(true);
        setIsMobileCommentPostOpen(false);
      }
    },
    [isAuthenticated, isMobile, localReactions, user?.id]
  );

  const handlePreviewDragStart = useCallback(() => {
    if (isPreview) {
      return;
    }
    setIsDraggingNew(true);
  }, [isPreview]);

  const handlePreviewDragEnd = useCallback(() => {
    if (isPreview) {
      return;
    }
    setIsDraggingNew(false);
  }, [isPreview]);

  const handleOverlayDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (isPreview || !isDraggingNew) {
        return;
      }

      event.preventDefault();
    },
    [isDraggingNew, isPreview]
  );

  const handleOverlayDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (isPreview || !isDraggingNew) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const overlay = overlayRef.current;
      if (!overlay) {
        return;
      }

      const data = event.dataTransfer.getData('text/plain');
      const rect = overlay.getBoundingClientRect();
      const clamp = (value: number) => Math.min(100, Math.max(0, value));

      const relativeX = ((event.clientX - rect.left) / rect.width) * 100;
      const relativeY = ((event.clientY - rect.top) / rect.height) * 100;

      const commentObject = data || scenarioCommentObject;

      const newReaction: CapsuleReaction = {
        id: `temp-${Date.now()}`,
        text: '',
        createdAt: new Date().toISOString(),
        author: buildAuthor(),
        position: {
          top: clamp(relativeY),
          left: clamp(relativeX),
        },
        commentObject,
        replies: [],
        isGuest: !isAuthenticated,
      };

      setLocalReactions(prev => [...prev, newReaction]);
      setActiveReactionId(newReaction.id);
      setIsDraggingNew(false);
      if (isAuthenticated) {
        setCommentDraft('');
      }
    },
    [
      buildAuthor,
      scenarioCommentObject,
      isDraggingNew,
      setLocalReactions,
      isAuthenticated,
      isPreview,
    ]
  );

  const canComment = !isPreview
    ? Boolean(
        isAuthenticated &&
          selectedComment &&
          !selectedComment.isGuest &&
          !selectedComment.text
      )
    : false;

  const canReply = !isPreview
    ? Boolean(
        isAuthenticated &&
          selectedComment &&
          !selectedComment.isGuest &&
          selectedComment.text
      )
    : false;

  const handleCommentSubmit = useCallback(async () => {
    if (!commentDraft.trim() || isPreview) return;
    pendingCommentSubmitRef.current = false;
    if (backgroundMode === 'tunnel') {
      if (!selectedTunnelStone) return;
      pendingStoneIdRef.current = selectedTunnelStone.id;

      // Важно: камень, который мы "покупаем", должен совпадать с `comment_object`,
      // который реально уйдёт на бэк при сабмите (иначе можно купить один камень,
      // а отправить комментарий с другим и получить 400).
      if (activeReactionId) {
        setLocalReactions(prev =>
          prev.map(reaction => {
            if (reaction.id !== activeReactionId) return reaction;
            if (reaction.text) return reaction;
            if (reaction.commentObject === selectedTunnelStone.id) {
              return reaction;
            }
            return { ...reaction, commentObject: selectedTunnelStone.id };
          })
        );
      }
      pendingCommentSubmitRef.current = true;
      setIsPurchaseConfirmOpen(true);
      return;
    }
    await submitCommentCore();
  }, [
    activeReactionId,
    backgroundMode,
    commentDraft,
    isPreview,
    selectedTunnelStone,
    submitCommentCore,
  ]);

  const handleReplySubmit = useCallback(async () => {
    if (!activeReactionId || !replyDraft.trim() || isPreview) {
      return;
    }

    const nextText = replyDraft.trim();
    const currentReaction = localReactions.find(r => r.id === activeReactionId);

    if (!currentReaction) {
      return;
    }

    try {
      const result = await capsulesApi.addCommentReply(
        capsule.id,
        activeReactionId,
        { text: nextText }
      );

      if (result.success && result.data) {
        const newReply = {
          id: result.data.id,
          text: result.data.text,
          createdAt: result.data.createdAt,
          author: result.data.author,
        };

        setLocalReactions(prev => {
          const updated = prev.map(reaction => {
            if (reaction.id === activeReactionId) {
              const updatedReaction = {
                ...reaction,
                replies: [...(reaction.replies || []), newReply],
              };
              return updatedReaction;
            }
            return reaction;
          });
          return updated;
        });
      }
    } catch {
      // ошибка сохранения ответа
    }

    setReplyDraft('');
  }, [activeReactionId, replyDraft, localReactions, capsule.id, isPreview]);

  const handleViewPickerSelect = useCallback(
    (option: string) => {
      const nextView = viewOptions.find(({ label }) => label === option)?.key;
      if (nextView) {
        onViewSelect(nextView);
      }
    },
    [onViewSelect, viewOptions]
  );

  useEffect(() => {
    if (!isMobile) {
      setIsMobileCommentPreviewVisible(false);
      setIsMobileCommentPostOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!selectedComment || isScenarioPreview) {
      setIsMobileCommentPreviewVisible(false);
      setIsMobileCommentPostOpen(false);
    }
  }, [selectedComment, isScenarioPreview]);

  const handleMobilePreviewExpand = useCallback(() => {
    if (backgroundMode === 'tunnel') {
      setTunnelCenterView('comment');
      setIsMobileCommentPostOpen(false);
      setIsMobileCommentPreviewVisible(false);
      return;
    }

    setIsMobileCommentPostOpen(true);
    setIsMobileCommentPreviewVisible(false);
  }, [backgroundMode]);

  const handleMobilePostClose = useCallback(() => {
    setIsMobileCommentPostOpen(false);
    setIsMobileCommentPreviewVisible(false);
  }, []);

  const shouldShowBottomControls =
    !isScenarioPreview &&
    !(isMobile && (isMobileCommentPreviewVisible || isMobileCommentPostOpen));
  const shouldShowCommentPanel = !isScenarioPreview;
  const canExpandCommentPanel = backgroundMode !== 'tunnel';
  const tunnelConfig = useMemo(
    () => ({
      bgColor: 0xdfded5,
      stoneCount: 60,
      tunnelRadius: isMobile ? 4 : 10,
      tunnelLength: isMobile ? 70 : 80,
      minDistance: 4,
      rotSpeed: 0.003,
      moveSpeed: 0.04,
      stonePlaneSize: isMobile ? 3 : 6,
    }),
    [isMobile]
  );

  return (
    <div
      className={cn(styles.wrapper, {
        [styles.wrapperFullscreen]: isFullscreen,
      })}
    >
      <div
        ref={overlayRef}
        className={cn(styles.canvas, {
          [styles.overlayDroppable]: isDraggingNew,
        })}
        onDragOver={handleOverlayDragOver}
        onDrop={handleOverlayDrop}
      >
        <div className={styles.background} onMouseDown={handlePan}>
          {backgroundMode === 'tunnel' ? (
            <TunnelBackground
              config={tunnelConfig}
              reactions={tunnelReactions}
              stoneTextureUrls={tunnelStoneTextureUrls}
              onStoneClick={handleReactionSelect}
              uiOverlayRefs={[tunnelCenterScreenRef, bottomControlsRef]}
            />
          ) : (
            <img
              src={getMediaUrl(backgroundSrc)}
              alt={t('capsuleReactions.backgroundAlt')}
              className={styles.backgroundImage}
            />
          )}
        </div>
      </div>
      <div className={styles.content}>
        {headerSlot ? (
          headerSlot
        ) : isScenarioPreview ? (
          <O_CapsuleCreateHeaderResponsive
            isScenarioPreview
            onPreviewClick={onPreviewClick}
            onPublishClick={onPublish}
          />
        ) : (
          <O_CapsuleReactionsHeaderResponsive
            onBack={() => {
              if (!isAuthenticated) {
                navigate(PATHS.AUTH, {
                  state: {
                    from: PATHS.CAPSULE_BY_ID(String(capsule.id)),
                    view: 'reactions',
                  },
                });
                return;
              }

              window.history.back();
            }}
            onExpand={() => {
              setIsExpandedView(prev => !prev);
              setActiveReactionId(null);
            }}
            isExpanded={isExpandedView}
            onOpenPostMenu={() => setIsPostMenuOpen(true)}
            showPostMenuButton={isMobile}
            viewOptions={viewOptionLabels}
            selectedViewOption={selectedViewLabel}
            onViewSelect={handleViewPickerSelect}
            showActionsMenu={isAuthor}
            onDelete={isAuthor ? handleDeleteCapsule : undefined}
            capsuleId={String(capsule.id)}
            showExpandButton={backgroundMode !== 'tunnel'}
          />
        )}
        {shouldShowCommentPanel ? (
          <CapsuleCommentSidebar
            isMobile={isMobile}
            selectedComment={selectedComment}
            isExpandedView={isExpandedView}
            canExpandCommentPanel={canExpandCommentPanel}
            rightPanelMaxHeight={rightPanelMaxHeight}
            capsuleMock={capsuleMock}
            localReactionsCount={localReactions.length}
            isAuthenticated={isAuthenticated}
            canComment={canComment}
            commentDraft={commentDraft}
            onCommentDraftChange={value => setCommentDraft(value)}
            onCommentSubmit={handleCommentSubmit}
            canReply={canReply}
            replyDraft={replyDraft}
            onReplyDraftChange={value => setReplyDraft(value)}
            onReplySubmit={handleReplySubmit}
            capsule={capsule}
          />
        ) : null}
        <Q_BlurEffect yAxis='top' withBackground={false} zIndex={-1} />
      </div>
      {backgroundMode === 'tunnel' &&
        !isPreview &&
        tunnelStoneOptions.length > 0 && (
          <div
            ref={tunnelCenterScreenRef}
            className={styles.tunnel_center_screen}
          >
            <TunnelCenterPanel
              tunnelCenterView={tunnelCenterView}
              selectedComment={selectedComment}
              tunnelStoneOptions={tunnelStoneOptions}
              selectedStoneId={selectedStoneId}
              onStoneSelect={handleTunnelStoneSelect}
              onPreviewDragStart={handlePreviewDragStart}
              onPreviewDragEnd={handlePreviewDragEnd}
              isStoneSelectorDisabled={
                isLoadingTunnelStones || isPurchasingStone
              }
              balance={user?.wallet?.balance ?? null}
              isAuthenticated={isAuthenticated}
              commentDraft={commentDraft}
              onCommentDraftChange={value => setCommentDraft(value)}
              onCommentSubmit={handleCommentSubmit}
              onBackToStones={() => setTunnelCenterView('stones')}
            />
          </div>
        )}
      {shouldShowBottomControls && (
        <div ref={bottomControlsRef} className={styles.bottom_controls}>
          <div className={styles.bottom_controls_left} />
          {!isPreview && !isMobile && (
            <div
              className={cn(styles.select_picker_wrapper, 'effect-green-glow')}
            >
              <M_SelectPicker
                options={viewOptionLabels}
                selectedOption={selectedViewLabel}
                onSelect={handleViewPickerSelect}
                background='white'
              />
            </div>
          )}
          {!isPreview && backgroundMode !== 'tunnel' && (
            <M_AddReaction
              commentObject={scenario?.commentObject ?? ReactionFallback}
              onPreviewDragStart={handlePreviewDragStart}
              onPreviewDragEnd={handlePreviewDragEnd}
            />
          )}
          <Q_BlurEffect withBackground={false} zIndex={-1} />
        </div>
      )}
      {backgroundMode === 'tunnel' && tunnelReactions.length > 0 && (
        <LiveTicker
          items={tunnelReactions}
          intervalMs={2000}
          onItemClick={handleReactionSelect}
        />
      )}
      {isMobile &&
        canExpandCommentPanel &&
        !isScenarioPreview &&
        selectedComment &&
        isMobileCommentPreviewVisible &&
        !isMobileCommentPostOpen && (
          <div className={styles.mobile_comment_preview}>
            <M_CapsuleCommentMobilePreview
              text={selectedComment.text || ''}
              onExpand={handleMobilePreviewExpand}
            />
          </div>
        )}
      <O_CapsulePostMenu
        isOpen={isPostMenuOpen}
        onClose={() => setIsPostMenuOpen(false)}
        capsule={capsuleMock}
        showActionsMenu={isAuthor}
        onDelete={isAuthor ? handleDeleteCapsule : undefined}
        capsuleId={String(capsule.id)}
      />
      <O_CapsuleCommentMobilePost
        isOpen={
          isMobile &&
          canExpandCommentPanel &&
          !isScenarioPreview &&
          isMobileCommentPostOpen &&
          Boolean(selectedComment)
        }
        onClose={handleMobilePostClose}
        capsule={capsuleMock}
        comment={selectedComment}
        commentCount={localReactions.length}
        plantCount={0}
        isAuthenticated={isAuthenticated}
        canComment={canComment}
        commentDraft={commentDraft}
        onCommentDraftChange={value => setCommentDraft(value)}
        onCommentSubmit={handleCommentSubmit}
        canReply={canReply}
        replyDraft={replyDraft}
        onReplyDraftChange={value => setReplyDraft(value)}
        onReplySubmit={handleReplySubmit}
      />
      <A_Notification
        type={stoneNotification?.type || 'success'}
        message={stoneNotification?.message || ''}
        isVisible={Boolean(stoneNotification)}
        onClose={() => setStoneNotification(null)}
      />
      <ConfirmStonePurchaseModal
        isOpen={isPurchaseConfirmOpen}
        stone={selectedTunnelStone}
        isLoading={isPurchasingStone}
        onCancel={() => {
          pendingCommentSubmitRef.current = false;
          setIsPurchaseConfirmOpen(false);
        }}
        onConfirm={handlePurchaseConfirm}
      />
    </div>
  );
};
